import { Styles } from './styles';
import Box from '@mui/material/Box';
import { SvgIcon, Typography, Button, TextField, FormControl, CircularProgress } from '@mui/material';
import { Close, EmailOutlined, CalendarMonthOutlined, Person2Outlined, CakeOutlined } from '@mui/icons-material';
import { useStyles } from '../../../theme/styles.helpers';
import { Orders, Utils } from '@hellohair/types';
import { useTranslation } from 'react-i18next';
import {
  copyTempSurvey,
  getFailedShopifyRequests,
  listOrderRequests,
  retryFailedShopifyOrder,
} from '../../../aws/RequestsApi';
import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../aws/AuthProvider';
import sanitize from 'sanitize-s3-objectkey';

type Props = {
  onModalClose: () => void;
  selectedOrder: Orders.ShopifyOrder | null;
  tempSurveys: Utils.SurveyMetaData[];
};

const FailedShopifyOrderModal = ({ onModalClose, selectedOrder, tempSurveys }: Props) => {
  const { t } = useTranslation();
  const styles = useStyles(Styles, {});
  const { apiClient } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [retryResult, setRetryResult] = useState<string>('');
  const [userOrderHistory, setUserOrderHistory] = useState<Utils.DBItem<Orders.OrderRequest>[] | null>(null);
  const [searchForms, setSearchForms] = useState<string>('');
  const [surveys, setSurveys] = useState<Utils.SurveyMetaData[]>(tempSurveys);
  const [localSurveys, setLocalSurveys] = useState<Utils.SurveyMetaData[]>(tempSurveys);

  const isSubscription = selectedOrder?.lineItems.some(
    (item) => item.sellingPlan !== null && item.sellingPlan !== undefined
  );

  useEffect(() => {
    if (!selectedOrder) return;
    listOrderRequests({
      apiClient,
      fetchPolicy: 'network-only',
      email: selectedOrder.customer.email,
    }).then((res) => {
      setUserOrderHistory(res);
      setLoading(false);
    });
  }, [selectedOrder]);

  useEffect(() => {
    if (searchForms) {
      const filteredSurveys = surveys.filter((survey) =>
        // String() - firstName/lastName are null on surveys taken before the
        // mail+DoB screen asked for a name, and null.toString() throws.
        Object.values(survey).some((value) =>
          String(value ?? '')
            .toLowerCase()
            .includes(searchForms.toLowerCase())
        )
      );
      setLocalSurveys(filteredSurveys);
    } else {
      setLocalSurveys(surveys);
    }
  }, [searchForms]);

  if (!selectedOrder) return null;

  const normalizedEmail = sanitize(selectedOrder.customer.email.toLowerCase().trim());
  const lastRequest = userOrderHistory?.[0];
  const customerHasValidSurvey = surveys?.find((survey) => survey.email === normalizedEmail);

  /**
   * `name` carries the patient name of the survey being reused, so the rebuilt
   * meta file keeps it - the order webhook prefers it over the Shopify checkout
   * name. Reusing an old request's assets has no survey name to pass on, and
   * then the webhook falls back to the checkout name as before.
   */
  const reuseAssets = (
    dob: string | undefined,
    assets: Orders.SurveyAsset[],
    name?: { firstName?: string; lastName?: string }
  ) => {
    if (!dob || !assets) return;
    setLoading(true);

    const tempSurveyData = {
      email: selectedOrder.customer.email,
      dob,
      assets: assets.map((a) => ({
        category: a.category,
        key: a.key,
      })),
      firstName: name?.firstName,
      lastName: name?.lastName,
    };

    copyTempSurvey(tempSurveyData, apiClient).then((copyRes) => {
      console.log('copyTempSurvey: ', copyRes);
      getFailedShopifyRequests(apiClient, 'network-only').then((res) => {
        console.log('res.tempSurveys: ', res.tempSurveys);
        setSurveys(res.tempSurveys);
        setLocalSurveys(res.tempSurveys);
        setLoading(false);
      });
    });
  };

  /**
   * The backend replays the events this order needs. A subscription needs two -
   * orders/create writes the pendingRequests entry that the Appstle
   * subscription.created event then reads back to restore the subscription - so
   * both are fired server-side, in order.
   */
  const retryOrder = () => {
    setLoading(true);

    retryFailedShopifyOrder(selectedOrder.id, apiClient)
      .then((result) => {
        console.log('retry order response: ', result);
        setRetryResult(result.message);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error retrying order:', error);
        setRetryResult('Error');
        setLoading(false);
      });
  };

  return (
    <Box sx={styles.root}>
      <Box sx={styles.container}>
        <Box sx={styles.header}>
          <Typography variant="h4">{selectedOrder.name}</Typography>
        </Box>

        <Box sx={styles.details}>
          <Box sx={styles.leftContainer}>
            <FormControl sx={{ width: '100%', marginBottom: 4 }} variant="outlined">
              <TextField
                label={t('Search temp forms')}
                variant="outlined"
                value={searchForms}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchForms(event.target.value)}
              />
            </FormControl>
            {localSurveys.map((survey, idx) => (
              <Box
                key={survey.requestId + idx}
                display="flex"
                flexDirection={'column'}
                justifyContent={'space-between'}
                mb={3}
                pb={3}
                borderBottom={1}
                borderColor={'grey.200'}
                gap={1}
              >
                <Box alignSelf={'start'}>
                  <Box display={'flex'} gap={2}>
                    <SvgIcon component={EmailOutlined}></SvgIcon>
                    <Typography>{survey.email}</Typography>
                  </Box>
                  {(survey.firstName || survey.lastName) && (
                    <Box display={'flex'} gap={2}>
                      <SvgIcon component={Person2Outlined}></SvgIcon>
                      <Typography>{[survey.firstName, survey.lastName].filter(Boolean).join(' ')}</Typography>
                    </Box>
                  )}
                  <Box display={'flex'} gap={2}>
                    <SvgIcon component={CakeOutlined}></SvgIcon>
                    <Typography>{new Date(survey.customerDob).toLocaleDateString('de-DE')}</Typography>
                  </Box>
                  <Box display={'flex'} gap={2}>
                    <SvgIcon component={CalendarMonthOutlined}></SvgIcon>
                    <Typography>{new Date(survey.createdAt).toLocaleString('de-DE')}</Typography>
                  </Box>
                </Box>
                <Box alignSelf={'end'}>
                  <Button
                    sx={styles.button}
                    disabled={loading || !!customerHasValidSurvey || !!retryResult || !survey.customerDob}
                    onClick={() =>
                      reuseAssets(survey.customerDob, survey.assetsList, {
                        firstName: survey.firstName,
                        lastName: survey.lastName,
                      })
                    }
                  >
                    <Typography>{t('Use this form')}</Typography>
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
          <Box sx={styles.rightContainer}>
            <Box display="flex" flexDirection={'column'} gap={4}>
              <Box>
                <Box display={'flex'} gap={2}>
                  <SvgIcon component={Person2Outlined}></SvgIcon>
                  <Typography>{`${selectedOrder.customer.firstName} ${selectedOrder.customer.lastName}`}</Typography>
                </Box>
                <Box display={'flex'} gap={2}>
                  <SvgIcon component={EmailOutlined}></SvgIcon>
                  <Typography>{selectedOrder.customer.email}</Typography>
                </Box>
                <Box display={'flex'} gap={2}>
                  <SvgIcon component={CalendarMonthOutlined}></SvgIcon>
                  <Typography>{new Date(selectedOrder.createdAt).toLocaleString('de-DE')}</Typography>
                </Box>
              </Box>

              {lastRequest ? (
                <Box>
                  <Typography>{t('Last success form')}</Typography>
                  <Typography>{lastRequest.product.productName}</Typography>
                  <Typography>{new Date(lastRequest.createdAt).toLocaleString('de-DE')}</Typography>
                  <Box mt={2} display="flex" gap={2}>
                    <Button
                      sx={styles.button}
                      disabled={loading || !!customerHasValidSurvey || !!retryResult}
                      onClick={() => reuseAssets(lastRequest.customer.birthDate, lastRequest.assets)}
                    >
                      <Typography>{t('Use this form')}</Typography>
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography>{t('No last success form')}</Typography>
                </Box>
              )}
            </Box>

            <Box display="flex" flexDirection={'column'} gap={1}>
              {isSubscription && (
                <Typography variant="body2">
                  {t('Subscription order - the Appstle subscription event is replayed as well')}
                </Typography>
              )}
              <Button
                sx={styles.button}
                disabled={!customerHasValidSurvey || loading || !!retryResult}
                onClick={retryOrder}
              >
                {loading ? (
                  <CircularProgress size={24} thickness={5} />
                ) : (
                  <Typography>{retryResult || t('Retry order')}</Typography>
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Closing Icon */}
      <Box onClick={onModalClose} sx={{ marginLeft: 2, cursor: 'pointer' }}>
        <SvgIcon sx={{ color: 'primary.main' }} component={Close}></SvgIcon>
      </Box>
    </Box>
  );
};

export default FailedShopifyOrderModal;
